// T023 — Revoke Credit dialog (US3). Includes optional target-batch selection.
import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { revokeCreditSchema } from "../validation";
import { formatEur, parseEurInput } from "../../../utils/eur";
import { formatDate } from "../../../utils/formats";
import type { AdminRevokeRequest, UpcomingExpiration } from "../../../types/wallet";

interface RevokeCreditDialogProps {
  visible: boolean;
  email: string;
  spendableBalance: string;
  upcomingExpirations: UpcomingExpiration[];
  loading: boolean;
  onSubmit: (request: AdminRevokeRequest) => Promise<void>;
  onHide: () => void;
}

interface BatchOption {
  label: string;
  value: string | null;
}

const RevokeCreditDialog = ({
  visible,
  email,
  spendableBalance,
  upcomingExpirations,
  loading,
  onSubmit,
  onHide,
}: RevokeCreditDialogProps) => {
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [targetBatchId, setTargetBatchId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setAmount(null);
      setNote("");
      setTargetBatchId(null);
      setError(null);
    }
  }, [visible]);

  const batchOptions = useMemo<BatchOption[]>(() => {
    const defaultOption: BatchOption = {
      label: "Any batch (oldest expiring first — default)",
      value: null,
    };
    const batchEntries: BatchOption[] = (upcomingExpirations ?? []).map((batch) => ({
      label: `${batch.batch_id.slice(0, 8)}… · ${formatEur(batch.amount)} · expires ${formatDate(batch.expires_at)}`,
      value: batch.batch_id,
    }));
    return [defaultOption, ...batchEntries];
  }, [upcomingExpirations]);

  const handleSubmit = async () => {
    setError(null);
    try {
      const validated = await revokeCreditSchema.validate({
        amount,
        note,
        target_batch_id: targetBatchId ?? undefined,
      });
      const request: AdminRevokeRequest = {
        amount: parseEurInput(validated.amount as number),
        note: (validated.note as string).trim(),
      };
      if (validated.target_batch_id) {
        request.target_batch_id = validated.target_batch_id;
      }
      await onSubmit(request);
    } catch (err: any) {
      setError(err?.message ?? "Could not revoke credits. Please try again.");
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" severity="secondary" text onClick={onHide} disabled={loading} />
      <Button
        label="Revoke credits"
        icon="pi pi-user-minus"
        severity="danger"
        onClick={handleSubmit}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="Revoke credits"
      visible={visible}
      onHide={onHide}
      style={{ width: "32rem" }}
      footer={footer}
      modal
      blockScroll
      closable={!loading}
    >
      <p className="text-sm text-color-secondary mt-0">
        Revoke credits from <strong>{email}</strong>. Spendable balance:{" "}
        <strong>{formatEur(spendableBalance)}</strong>. If the requested amount exceeds the
        balance, the backend clamps at zero and the response shows both numbers.
      </p>

      <div className="field">
        <label htmlFor="revoke-amount" className="font-medium">Amount (EUR)</label>
        <InputNumber
          inputId="revoke-amount"
          value={amount}
          onValueChange={(event) => setAmount(event.value ?? null)}
          mode="currency"
          currency="EUR"
          locale="de-DE"
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0.01}
          showButtons
          className="w-full mt-1"
        />
      </div>

      <div className="field">
        <label htmlFor="revoke-batch" className="font-medium">Revoke from batch (optional)</label>
        <Dropdown
          inputId="revoke-batch"
          value={targetBatchId}
          options={batchOptions}
          onChange={(event) => setTargetBatchId(event.value ?? null)}
          placeholder="Any batch (oldest expiring first — default)"
          className="w-full mt-1"
        />
      </div>

      <div className="field">
        <label htmlFor="revoke-note" className="font-medium">Note (required)</label>
        <InputTextarea
          id="revoke-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder='e.g. "fraudulent referral REF-9911"'
          className="w-full mt-1"
        />
      </div>

      {error && <Message severity="error" text={error} className="w-full" />}
    </Dialog>
  );
};

export default RevokeCreditDialog;
